import { useState } from "react";
import { Link, useParams } from "react-router";
import { AvaliacaoDoArtigo, Markdown } from "../componentes/conhecimento";
import { Abas, Aviso, Botao, Campo, Chip, PainelDaAba } from "../componentes/ui";
import { MINIMO_DE_LETRAS_DA_PERGUNTA, useAssistenteDoPortal } from "../nucleo/assistenteDoPortal";
import {
  MINIMO_DE_LETRAS_DA_BUSCA,
  ROTULO_DA_ORIGEM_DA_BUSCA,
  useArtigoDaAjuda,
  useBuscaNaAjuda,
  useBuscaUnificada,
  useCategoriasDaAjuda,
  useValorAdiado,
  type ItemDaBusca,
  type OrigemDaBusca,
} from "../nucleo/conhecimento";
import { mensagemDoConsole } from "../nucleo/console";
import { enderecoSeguro } from "../nucleo/markdown";
import { useTitulo } from "../nucleo/titulo";

type AbaDaBusca = "tudo" | OrigemDaBusca;

function dia(valor: string): string {
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "long" }).format(data);
}

/** O servidor só manda caminho do próprio portal; qualquer outra coisa não vira link. */
function rotaSegura(rota: string): string | null {
  return rota.startsWith("/app/") && !rota.startsWith("//") ? rota : null;
}

/**
 * O assistente do portal: a resposta vem da base de conhecimento e cita a fonte. Quando não há
 * fonte, a tela diz que o assistente não sabe — e isso é o comportamento certo, não um erro.
 */
function AssistenteDoPortal({ pergunta }: { pergunta: string }) {
  const resposta = useAssistenteDoPortal(pergunta);
  const digitou = pergunta.trim().length >= MINIMO_DE_LETRAS_DA_PERGUNTA;

  return (
    <section className="cartao assistente-do-portal" aria-labelledby="titulo-do-assistente">
      <h2 className="titulo-secao" id="titulo-do-assistente">
        Assistente do portal
      </h2>
      {!digitou ? (
        <p className="cartao__texto" data-testid="assistente-ocioso">
          Digite sua dúvida e eu respondo com base na documentação da GEOvendas.
        </p>
      ) : resposta.isPending ? (
        <p role="status" className="cartao__texto">
          Procurando na documentação…
        </p>
      ) : resposta.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(resposta.error)}</Aviso>
      ) : resposta.data.encontrou ? (
        <div data-testid="assistente-resposta" data-modo={resposta.data.modo}>
          <p className="cartao__texto texto-preservado">{resposta.data.resposta}</p>
          <p className="nota-rodape mt-2">
            {resposta.data.modo === "trechos"
              ? "Trecho da documentação. Fonte: "
              : "Resposta com base na documentação. Fonte: "}
            {resposta.data.fontes.map((fonte, indice) => (
              <span key={fonte.id}>
                {indice > 0 ? " · " : ""}
                <Link to={`/app/ajuda/${fonte.id}`}>{fonte.titulo}</Link>
              </span>
            ))}
          </p>
        </div>
      ) : (
        <div data-testid="assistente-recusa" data-motivo={resposta.data.motivo_da_recusa ?? ""}>
          <p className="cartao__texto">
            <b>{resposta.data.resposta}</b>
          </p>
          <p className="nota-rodape mt-2">
            Nenhuma fonte correspondente — resposta recusada de propósito.{" "}
            <Link to="/app/chamados">Abrir um chamado</Link>
          </p>
        </div>
      )}
    </section>
  );
}

function ResultadoDaBusca({ item }: { item: ItemDaBusca }) {
  const rota = rotaSegura(item.rota);
  /* O artigo mantém o `data-testid` que sempre teve na Central: quem procura "o cartão do
     artigo" encontra o mesmo, com ou sem a busca unificada. */
  const testid = item.origem === "ajuda" ? "artigo-da-ajuda" : "resultado-da-busca";
  const corpo = (
    <div className="flex items-start justify-between gap-3">
      <div>
        <strong>{item.titulo}</strong>
        {item.trecho ? <p className="cartao__texto mt-1">{item.trecho}</p> : null}
        <p className="nota-rodape mt-1">
          {item.modulo ? `${item.modulo} · ` : ""}
          {item.quando ? `Atualizado em ${dia(item.quando)}` : ""}
        </p>
      </div>
      <Chip estado="neutro" semPonto>
        {ROTULO_DA_ORIGEM_DA_BUSCA[item.origem]}
      </Chip>
    </div>
  );
  return rota ? (
    <Link className="cartao bloco-link" to={rota} data-testid={testid} data-origem={item.origem}>
      {corpo}
    </Link>
  ) : (
    <div className="cartao" data-testid={testid} data-origem={item.origem}>
      {corpo}
    </div>
  );
}

/** A busca da tela de ajuda: central, materiais e chamados da própria empresa, em abas. */
function BuscaUnificada({ texto, soMeusModulos }: { texto: string; soMeusModulos: boolean }) {
  const busca = useBuscaUnificada(texto, soMeusModulos);
  const [aba, setAba] = useState<AbaDaBusca>("tudo");

  if (busca.isPending) return <p role="status">Procurando…</p>;
  if (busca.isError) return <Aviso tom="erro">{mensagemDoConsole(busca.error)}</Aviso>;

  const { itens, totais } = busca.data;
  const total = totais.ajuda + totais.materiais + totais.chamados;
  const visiveis = aba === "tudo" ? itens : itens.filter((item) => item.origem === aba);

  return (
    <section aria-labelledby="titulo-artigos-da-ajuda">
      <h2 className="titulo-secao" id="titulo-artigos-da-ajuda">
        Resultados da busca
      </h2>
      <Abas<AbaDaBusca>
        id="busca-da-ajuda"
        rotulo="Onde procurar"
        ativa={aba}
        aoSelecionar={setAba}
        itens={[
          { chave: "tudo", rotulo: "Tudo", contagem: total },
          { chave: "ajuda", rotulo: ROTULO_DA_ORIGEM_DA_BUSCA.ajuda, contagem: totais.ajuda },
          { chave: "materiais", rotulo: ROTULO_DA_ORIGEM_DA_BUSCA.materiais, contagem: totais.materiais },
          { chave: "chamados", rotulo: ROTULO_DA_ORIGEM_DA_BUSCA.chamados, contagem: totais.chamados },
        ]}
      />
      <PainelDaAba id="busca-da-ajuda" chave={aba} ativa={aba}>
        {visiveis.length === 0 ? (
          <Aviso tom="marca" data-testid="ajuda-sem-resultado">
            {aba === "tudo"
              ? "Não encontramos nada para essa busca. Tente outras palavras ou "
              : "Nada encontrado nessa origem. O assistente acima pode tentar, ou você pode "}
            <Link to="/app/chamados">abrir um chamado</Link>.
          </Aviso>
        ) : (
          <div className="lista-de-itens mt-3">
            {visiveis.map((item) => (
              <ResultadoDaBusca key={`${item.origem}-${item.id}`} item={item} />
            ))}
          </div>
        )}
      </PainelDaAba>
      {soMeusModulos && busca.data.modulos_da_empresa.length > 0 ? (
        <p className="nota-rodape mt-3" data-testid="modulos-da-busca">
          Mostrando o que se aplica aos seus módulos:{" "}
          {busca.data.modulos_da_empresa.map((m) => m.nome).join(", ")} — e o que vale para todos.
        </p>
      ) : null}
    </section>
  );
}

/** A Central de ajuda do cliente: pergunta ao assistente, busca unificada e a lista de artigos. */
export function CentralDeAjuda() {
  useTitulo("Central de ajuda");
  const [texto, setTexto] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  /* Ligado por padrão: o cliente não vê material de módulo que não tem. */
  const [soMeusModulos, setSoMeusModulos] = useState(true);
  const buscado = useValorAdiado(texto.trim(), 300);
  /* A pergunta ao assistente espera mais: a cada palavra nova seria uma pergunta nova. */
  const perguntado = useValorAdiado(texto.trim(), 800);
  const categorias = useCategoriasDaAjuda(soMeusModulos);
  const artigos = useBuscaNaAjuda("", categoriaId, soMeusModulos);
  const procurando = buscado.length >= MINIMO_DE_LETRAS_DA_BUSCA;

  return (
    <div data-testid="central-de-ajuda">
      <h1 className="titulo-pagina">Central de ajuda</h1>
      <p className="lead">
        Encontre respostas e passo a passo sem esperar. Não achou? Abra um chamado e a equipe da
        GEOvendas atende você.
      </p>

      <div className="mb-3">
        <Campo
          rotulo="Buscar na central de ajuda"
          type="search"
          value={texto}
          placeholder="Ex.: emissão de pedido, tabela de preços"
          onChange={(evento) => setTexto(evento.target.value)}
        />
      </div>
      <label className="campo-marcavel mb-4">
        <input
          type="checkbox"
          checked={soMeusModulos}
          onChange={(evento) => setSoMeusModulos(evento.target.checked)}
        />{" "}
        Só o que se aplica aos meus módulos
      </label>

      <div className="mb-5">
        <AssistenteDoPortal pergunta={perguntado} />
      </div>

      {procurando ? (
        <BuscaUnificada texto={buscado} soMeusModulos={soMeusModulos} />
      ) : (
        <>
          {categorias.data && categorias.data.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label="Categorias">
              <Botao
                variante={categoriaId === "" ? "solido" : "fantasma"}
                tamanho="pequeno"
                aria-pressed={categoriaId === ""}
                onClick={() => setCategoriaId("")}
              >
                Todas
              </Botao>
              {categorias.data.map((categoria) => (
                <Botao
                  key={categoria.id}
                  variante={categoriaId === categoria.id ? "solido" : "fantasma"}
                  tamanho="pequeno"
                  aria-pressed={categoriaId === categoria.id}
                  onClick={() => setCategoriaId(categoria.id)}
                >
                  {categoria.nome}
                  {categoria.artigos != null ? ` (${categoria.artigos})` : ""}
                </Botao>
              ))}
            </div>
          ) : null}

          <section aria-labelledby="titulo-artigos-da-ajuda">
            <h2 className="titulo-secao" id="titulo-artigos-da-ajuda">
              Artigos
            </h2>
            {artigos.isPending ? (
              <p role="status">Carregando artigos…</p>
            ) : artigos.isError ? (
              <Aviso tom="erro">{mensagemDoConsole(artigos.error)}</Aviso>
            ) : artigos.data.length === 0 ? (
              <Aviso tom="marca" data-testid="ajuda-sem-resultado">
                Ainda não há artigos publicados. <Link to="/app/chamados">abra um chamado</Link>.
              </Aviso>
            ) : (
              <div className="lista-de-itens">
                {artigos.data.map((artigo) => (
                  <Link
                    key={artigo.id}
                    className="cartao bloco-link"
                    to={`/app/ajuda/${artigo.id}`}
                    data-testid="artigo-da-ajuda"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <strong>{artigo.titulo}</strong>
                        {artigo.resumo ? <p className="cartao__texto mt-1">{artigo.resumo}</p> : null}
                        <p className="nota-rodape mt-1">
                          {artigo.modulo ? `${artigo.modulo} · ` : ""}Atualizado em{" "}
                          {dia(artigo.atualizado_em)}
                        </p>
                      </div>
                      {artigo.categoria ? <Chip estado="neutro">{artigo.categoria}</Chip> : null}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** Um artigo da Central, com o "isso ajudou?" no fim. */
export function ArtigoDaCentral() {
  const { id = "" } = useParams();
  const artigo = useArtigoDaAjuda(id);
  useTitulo(artigo.data?.titulo ?? "Central de ajuda");

  if (artigo.isPending) return <p role="status">Abrindo o artigo…</p>;
  if (artigo.isError) {
    return (
      <div>
        <Aviso tom="erro">{mensagemDoConsole(artigo.error)}</Aviso>
        <p className="mt-4">
          <Link to="/app/ajuda">Voltar para a central de ajuda</Link>
        </p>
      </div>
    );
  }
  const item = artigo.data;
  /* O servidor só manda o link da origem quando o cliente consegue abri-lo; a tela ainda confere
     o esquema (só http/https) antes de pôr o endereço num `href`. */
  const origemAberta = item.origem_url ? enderecoSeguro(item.origem_url) : null;
  return (
    <article data-testid="artigo-aberto">
      <p className="nota-rodape">
        <Link to="/app/ajuda">Central de ajuda</Link>
        {item.categoria ? ` · ${item.categoria}` : ""}
      </p>
      <h1 className="titulo-pagina">{item.titulo}</h1>
      <p className="nota-rodape mb-4">
        {item.modulo ? `${item.modulo} · ` : ""}Atualizado em {dia(item.atualizado_em)}
      </p>
      <Markdown texto={item.corpo} data-testid="corpo-do-artigo" />
      {origemAberta ? (
        <p className="nota-rodape mt-4" data-testid="ver-na-origem">
          <a href={origemAberta} target="_blank" rel="noopener noreferrer">
            Ver este artigo na central de ajuda
          </a>{" "}
          (com as imagens).
        </p>
      ) : null}
      <AvaliacaoDoArtigo artigoId={item.id} minha={item.minha_avaliacao} />
      <p className="nota-rodape mt-6">
        Ainda com dúvida? <Link to="/app/chamados">Abra um chamado</Link>.
      </p>
    </article>
  );
}
